import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../props";
import querystring from "querystring";
import { pick } from "ramda";
import { useMutation, useQueryClient } from "react-query";
import {
  useManualGet,
  doDelete,
  useGet,
  useUpdate,
  usePost,
  useUpload,
  usePut,
  useDelete,
} from "../../common/fetchServices";
import { useCurrentOrgId } from "../../auth/authUtils";
import { getQueryParams } from "../../util/fetchUtils";
import { useDeleteActiveModule } from "../../manage-organizations/organizationServices";

const getCreateModuleUrl = ({ orgId = null }) => {
  const url = `${API_BASE_URL}/admin/modules`;
  return orgId ? url + `?org-id=${orgId}` : url;
};
const getSuggestInternalCodeUrl = () =>
  `${API_BASE_URL}/admin/modules/suggest-internal-code`;
const getModuleUrl = id => `${API_BASE_URL}/modules/${id}`;
const getModuleAdminUrl = id => `${API_BASE_URL}/admin/modules/${id}`;
const getPatchModuleUrl = ({ id }) => `${API_BASE_URL}/admin/modules/${id}`;
const getModuleMediaUrl = ({ id }) =>
  `${API_BASE_URL}/admin/modules/${id}/media`;
const getModuleContentUrl = id =>
  `${API_BASE_URL}/admin/modules/${id}/media/content`;
const getModuleContentCountUrl = id =>
  `${API_BASE_URL}/admin/modules/${id}/media/content-count`;
const getAllModuleContentUrl = id =>
  `${API_BASE_URL}/admin/modules/${id}/media/all-content`;
const getAllModuleContentCountUrl = id =>
  `${API_BASE_URL}/admin/modules/${id}/media/all-content/count`;
const getModuleDirectChildrenUrl = id =>
  `${API_BASE_URL}/modules/${id}/direct-children`;
const getThumbnailUploadUrl = ({ id }) =>
  `${API_BASE_URL}/admin/modules/${id}/thumbnail`;
const getModuleShowcaseUrl = ({ id }) =>
  `${API_BASE_URL}/admin/media/showcase-order/${id}`;
const getModuleMediaIdsUrl = id =>
  `${API_BASE_URL}/admin/modules/${id}/media/ids`;
const getModuleReloadUrl = ({ id }) =>
  `${API_BASE_URL}/admin/search/trigger-update?module-id=${id}&all-children=true`;

const buildModulesAdminUrl = params => {
  let url;

  const requiredParams = pick(["limit", "offset", "sort"], params);
  url = `${API_BASE_URL}/admin/modules?${querystring.encode(requiredParams)}`;

  return url;
};

export const useModuleAdmin = id => {
  const url = getModuleAdminUrl(id);
  return useGet(["module-admin", { id }], url);
};

export const useModule = id => {
  const url = getModuleUrl(id);
  return useGet(["module", { id }], url);
};

export const useModules = () => {
  const url = `${getCreateModuleUrl()}?limit=all`;
  return useGet(["modules"], url);
};

export const useModulesAdmin = ({
  sort = "auditUpdated desc",
  limit = 10,
  offset = 0,
}) => {
  const orgId = useCurrentOrgId();
  const url = buildModulesAdminUrl({
    sort,
    limit,
    offset,
  });

  return useGet(["modules-admin", orgId, { url }], url);
};

export const useModuleMedia = (id, { enabled = true } = {}) => {
  const orgId = useCurrentOrgId();
  const url = getModuleMediaUrl({ id });
  return useGet(["moduleMedia", orgId, { id }], url, enabled);
};

export const useHandleModuleMediaClick = ({
  id,
  onSuccess,
  queryOptions = {},
}) => {
  const orgId = useCurrentOrgId();
  const url = getModuleMediaUrl({ id });

  return useManualGet(
    ["moduleMedia", orgId, { id }],
    url,
    onSuccess,
    queryOptions,
  );
};

export const useModuleDirectChildren = id => {
  const url = getModuleDirectChildrenUrl(id);
  return useGet(["moduleDirectChildren", { id }], url);
};

export const useModuleContent = (id, params = {}) => {
  const queryParams = "?" + getQueryParams(params);
  const url = getModuleContentUrl(id) + queryParams;
  return useGet(["moduleContent", { url }], url, true, true);
};

export const useModuleContentCount = (id, params) => {
  const queryParams = "?" + getQueryParams(params);
  const url = getModuleContentCountUrl(id) + queryParams;
  return useGet(["module-content-count", { url }], url);
};

// returns direct and indirect content of a module
export const useAllModuleContent = (id, params = {}) => {
  const queryParams = "?" + getQueryParams(params);
  const url = getAllModuleContentUrl(id) + queryParams;
  return useGet(["module-all-content", { url }], url, true, true);
};

export const useAllModuleContentCount = (id, params) => {
  const queryParams = "?" + getQueryParams(params);
  const url = getAllModuleContentCountUrl(id) + queryParams;
  return useGet(["module-all-content-count", { url }], url);
};

export const useModuleUpdate = () => {
  return useUpdate(getPatchModuleUrl, {
    queriesToInvalidate: [
      "search",
      ["module", ["id"]],
      ["module-admin", ["id"]],
      "modules-admin",
      "admin-media",
      ["modules-creator"],
    ],
  });
};

export const useModuleCreate = () => {
  return usePost(getCreateModuleUrl, {
    queriesToInvalidate: [
      "search",
      "admin-media",
      "modules-admin",
      "modules-creator",
    ],
  });
};

export const useSuggestInternalCode = () => {
  return usePost(getSuggestInternalCodeUrl, {
    queriesToInvalidate: ["search"],
  });
};

export const useUploadModuleThumbnail = () => {
  return useUpload(getThumbnailUploadUrl, {
    props: {
      paramName: "file",
      method: "POST",
    },
  });
};

export const useAddToModule = () => {
  return usePost(getModuleMediaUrl, {
    queriesToInvalidate: [
      "admin-media",
      "anthology",
      ["anthologyApprovedMedia"],
      "anthologyCollections",
      ["anthology-collections-creator"],
      "collection",
      ["collection-content-type-summary"],
      "content-status",
      ["module", ["id"]],
      ["module-all-content"],
      ["module-all-content-count"],
      "moduleApprovedContent",
      ["moduleContent"],
      ["module-content-count"],
      ["module-content-groups"],
      "moduleMedia",
      "search",
      ["unorganized-content-groups"],
    ],
  });
};

export const useModuleShowcase = id => {
  const url = getModuleShowcaseUrl({ id });
  return useGet(["module-showcase", { id }], url);
};

export const useUpdateModuleShowcase = () => {
  return usePut(getModuleShowcaseUrl, {
    queriesToInvalidate: ["module-showcase", ["module-showcase", ["id"]]],
  });
};

export const useDeleteModuleItems = () => {
  const auth = useSelector(state => state.auth);
  const queryClient = useQueryClient();
  const currentOrgId = useCurrentOrgId();

  return useMutation(
    ({ id, ids, stopInvalidation }) => {
      const url = getModuleMediaIdsUrl(id);
      return doDelete(auth)(url, {
        data: { ids: ids },
      });
    },
    {
      onSuccess: (data, variables) => {
        const { id, stopInvalidation = false } = variables;

        queryClient.invalidateQueries("search");
        queryClient.invalidateQueries(["module", { id }]);
        queryClient.invalidateQueries("moduleMedia");
        queryClient.invalidateQueries(["moduleApprovedContent", { id }]);
        queryClient.invalidateQueries("anthologyApprovedMedia");
        queryClient.invalidateQueries("collection-approved-content");
        queryClient.invalidateQueries("admin-media");
        queryClient.invalidateQueries(["content-status", currentOrgId]);
        queryClient.invalidateQueries(["moduleContent"]);
        queryClient.invalidateQueries(["module-content-count"]);
        queryClient.invalidateQueries(["module-content-groups"]);
        queryClient.invalidateQueries(["unorganized-content-groups"]);
        if (!stopInvalidation) {
          queryClient.invalidateQueries(["module-all-content"]);
          queryClient.invalidateQueries(["module-all-content-count"]);
        }
      },
    },
  );
};

export const useRemoveAllModuleMedia = () => {
  const currentOrgId = useCurrentOrgId();
  return useDelete(getModuleMediaUrl, {
    queriesToInvalidate: [
      "admin-media",
      "anthologyApprovedMedia",
      "collection-approved-content",
      ["content-status", currentOrgId],
      ["module", ["id"]],
      ["moduleApprovedContent", ["id"]],
      ["moduleContent"],
      ["module-content-count"],
      ["module-content-groups"],
      "moduleMedia",
      "search",
    ],
  });
};

export const useModuleDelete = () => {
  const queryClient = useQueryClient();
  const updateModule = useModuleUpdate();
  const removeAllMediaFromModule = useRemoveAllModuleMedia();
  const deleteActiveModule = useDeleteActiveModule();

  return useMutation(
    async ({ module, orgId }) => {
      // remove all children and any active subscription before "deleting"
      await removeAllMediaFromModule.mutateAsync({
        resourceIds: { id: module.id },
      });
      await deleteActiveModule.mutateAsync({
        resourceIds: {
          moduleId: module.id,
          orgId: orgId,
        },
      });
      return updateModule.mutateAsync({
        resourceIds: { id: module.id },
        data: {
          status: "DELETED",
        },
      });
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries("search");
        const { moduleId } = variables;
        queryClient.invalidateQueries(["module", { id: moduleId }]);
        queryClient.invalidateQueries("moduleMedia");
        queryClient.invalidateQueries("moduleApprovedContent");
        queryClient.invalidateQueries("anthologyCollections");
        queryClient.invalidateQueries(["anthology-collections-creator"]);
        queryClient.invalidateQueries("content-status");
        queryClient.invalidateQueries("admin-media");
        queryClient.invalidateQueries("collection");
        queryClient.invalidateQueries("anthology");
        queryClient.invalidateQueries(["module-content-groups"]);
        queryClient.invalidateQueries(["collection-content-type-summary"]);
        queryClient.invalidateQueries(["anthologyApprovedMedia"]);
        queryClient.invalidateQueries(["unorganized-content-groups"]);
        queryClient.invalidateQueries(["moduleContent"]);
        queryClient.invalidateQueries(["module-content-count"]);
        queryClient.invalidateQueries(["modules-creator"]);
      },
    },
  );
};

export const useModuleReload = () => usePost(getModuleReloadUrl);
